import { ResultOf } from "gql.tada";
import {
  GetHomepageHeroById,
  GetHomepageIntroBenefitsById,
  GetHomepageAudienceCulturalById,
  GetHomepageProductMotionById,
  GetHomepageCarouselsById,
  GetHomepageSlidesCarouselById,
} from "@queries/archetypes/GetHomepageById";
import { SlidingThumbnailHero } from "@components/entries/SlidingThumbnailHero";
import { AudienceCulturalInfluenceSection } from "@components/entries/AudienceCulturalInfluenceSection";
import { IntroBenefitsSection } from "@components/entries/IntroBenefit/IntroBenefitsSection";
import { SlideCarouselSection } from "@components/entries/SlideCarouselSection";
import { NewsReelCard } from "@components/entries/NewsReelCard";
import { ProductMotionSlidesSection } from "@components/entries/ProductMotionSlidesSection";
import { TabbedCarouselSection } from "@components/entries/TabbedCarouselSection";
import { RotaryCarouselSection } from "@components/entries/RotaryCarouselSection";
import { TitleSection } from "@components/entries/TitleSection";
import { useLocaleRestriction } from "@hooks/useLocaleRestriction";

type HomepageHeroData = NonNullable<
  ResultOf<typeof GetHomepageHeroById>["homepage"]
>;

type HomepageIntroBenefitsData = NonNullable<
  ResultOf<typeof GetHomepageIntroBenefitsById>["homepage"]
>;

type HomepageAudienceCulturalData = NonNullable<
  ResultOf<typeof GetHomepageAudienceCulturalById>["homepage"]
>;

type HomepageProductMotionData = NonNullable<
  ResultOf<typeof GetHomepageProductMotionById>["homepage"]
>;

type HomepageCarouselsData = NonNullable<
  ResultOf<typeof GetHomepageCarouselsById>["homepage"]
>;

type HomepageSlidesCarouselData = NonNullable<
  ResultOf<typeof GetHomepageSlidesCarouselById>["homepage"]
>;

export type HomepageData = {
  hero: HomepageHeroData;
  introBenefits: HomepageIntroBenefitsData;
  audienceCultural: HomepageAudienceCulturalData;
  productMotion: HomepageProductMotionData;
  carousels: HomepageCarouselsData;
  slidesCarousel: HomepageSlidesCarouselData;
};

export const HomepageRenderer = ({ data }: { data: HomepageData }) => {
  const {
    hero,
    introBenefits,
    audienceCultural,
    productMotion,
    carousels,
    slidesCarousel,
  } = data;

  const { isComponentAllowed } = useLocaleRestriction();

  return (
    <>
      {hero.hero && (
        <SlidingThumbnailHero
          entry={{ slidingThumbnailHero: hero.hero } as any}
        />
      )}
      {introBenefits.introBenefits && (
        <IntroBenefitsSection
          entry={{ introBenefitsSection: introBenefits.introBenefits } as any}
        />
      )}
      {isComponentAllowed("homepage.newsReelSection") &&
        slidesCarousel.slidesCarousel && (
          <div className="px-6 dsk:px-[90px]">
            <SlideCarouselSection
              data={slidesCarousel.slidesCarousel}
              renderCard={(args) => <NewsReelCard {...args} />}
            />
          </div>
        )}
      {hero.sectionTitle && (
        <TitleSection entry={{ titleSection: hero.sectionTitle } as any} />
      )}
      {carousels.statisticsCarousel && (
        <RotaryCarouselSection
          entry={
            {
              rotaryCarouselSection: carousels.statisticsCarousel,
            } as any
          }
        />
      )}
      {audienceCultural.audienceCultural && (
        <AudienceCulturalInfluenceSection
          entry={
            {
              audienceCulturalInfluenceSection:
                audienceCultural.audienceCultural,
            } as any
          }
        />
      )}
      {productMotion.productMotion && (
        <ProductMotionSlidesSection
          entry={
            { productMotionSlidesSection: productMotion.productMotion } as any
          }
        />
      )}
      {carousels.tabbedCarousel && (
        <TabbedCarouselSection
          entry={{ tabbedCarouselSection: carousels.tabbedCarousel } as any}
        />
      )}
    </>
  );
};
